import { Component } from 'js-component-framework';
import { AriaDialog } from 'js-component-framework/lib/plugins';
import './modal.scss';

const getFormSubmission = () => import(
  /* webpackChunkName: "modal-submit-jetpack-form" */
  './modal-submit-jetpack-form'
);

/**
 * Component for a modal dialog box/popup window.
 */
export default class Modal extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    // Elements.
    const siteWrapperElement = document.querySelector('.site-content-wrapper');
    const { modalElement, closeElement, triggerElement } = this.children;

    if (! siteWrapperElement
      || ! modalElement
      || ! closeElement
      || ! triggerElement) {
      return;
    }

    // Bindings.
    this.setModalOpen = this.setModalOpen.bind(this);
    this.setModalClosed = this.setModalClosed.bind(this);

    // Initialize modal.
    this.modal = new AriaDialog({
      dialog: modalElement,
      close: closeElement,
      content: siteWrapperElement,
    });

    // Events.
    this.element.addEventListener('dialoghide', this.setModalClosed);
    triggerElement.addEventListener('click', (event) => {
      if ('A' === event.target.nodeName) {
        event.preventDefault();
      }

      const hasForm = Boolean(this.element.querySelector('form'));
      const hasJetpackForm = Boolean(
        this.element.querySelector('form.contact-form'),
      );

      if (hasForm) {
        this.setModalFormCustomizations();
      }

      if (hasJetpackForm) {
        // "Dynamically" load the jetpack contact form submission AJAX script.
        Promise.all([getFormSubmission()])
          .then(() => {
            this.setModalOpen();
          });
      } else {
        this.setModalOpen();
      }
    });
  }

  /**
   * Set modal to open state.
   */
  setModalOpen() {
    this.children.modalElement.style.display = 'block';
    document.body.classList.add(this.options.activeClass);
    this.modal.ariaShow();
  }

  /**
   * Set modal to closed state.
   */
  setModalClosed() {
    this.children.modalElement.style.display = 'none';
    document.body.classList.remove(this.options.activeClass);
  }

  /**
   * Set modal form customizations.
   */
  setModalFormCustomizations() {
    // Form.
    const form = this.element.querySelector('form');
    if (! form) {
      return;
    }
    form.classList.add('modal-form');

    // Labels.
    const labels = form.querySelectorAll('label');
    if (labels || (labels instanceof NodeList)) {
      Array.prototype.forEach.call(labels, (label) => {
        if (! label) {
          return;
        }

        label.classList.add('modal-form__label');

        const labelParent = label.parentElement;
        if (labelParent) {
          labelParent.classList.add('modal-form__label-parent');
        }

        const inputTextFields = [
          'text',
          'email',
        ];

        const labelNextSibling = label.nextElementSibling;
        if (labelNextSibling) {
          let labelType;
          if ('TEXTAREA' === labelNextSibling.nodeName) {
            labelType = 'textarea';
          }

          if ('INPUT' === labelNextSibling.nodeName
          && inputTextFields.includes(labelNextSibling.type)) {
            labelType = 'input';
          }

          if (labelParent && labelType) {
            labelParent.classList.add(`modal-form__label-parent--${labelType}`);
          }
        }
      });
    }

    // Input fields.
    const inputFields = form.querySelectorAll('input');
    if (inputFields || (inputFields instanceof NodeList)) {
      Array.prototype.forEach.call(inputFields, (input) => {
        const inputParent = input.parentElement;
        input.addEventListener('focus', () => {
          if (inputParent) {
            inputParent.classList.add('modal-form__label-parent--focus-active');
          }
        });

        input.addEventListener('blur', () => {
          if (inputParent) {
            inputParent.classList.remove(
              'modal-form__label-parent--focus-active',
            );
          }
          if (input.value) {
            inputParent.classList.add('modal-form__label-parent--filled');
          } else {
            inputParent.classList.remove('modal-form__label-parent--filled');
          }
        });
      });
    }

    // Textarea fields.
    const textAreaFields = form.querySelectorAll('textarea');
    if (textAreaFields || (textAreaFields instanceof NodeList)) {
      Array.prototype.forEach.call(textAreaFields, (textarea) => {
        textarea.addEventListener('focus', () => {
          const textareaParent = textarea.parentElement;
          if (textareaParent) {
            textareaParent.classList.add(
              'modal-form__label-parent--focus-active',
            );
          }
        });
        textarea.addEventListener('blur', () => {
          const textareaParent = textarea.parentElement;
          if (textareaParent) {
            textareaParent.classList.remove(
              'modal-form__label-parent--focus-active',
            );
          }

          if (textarea.value) {
            textareaParent.classList.add('modal-form__label-parent--filled');
          } else {
            textareaParent.classList.remove('modal-form__label-parent--filled');
          }
        });
      });
    }
  }
}
